import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { MapContainer, TileLayer } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import { Radar, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageSkeleton } from "../components/PageSkeleton";
import { CourierList } from "../components/tracking/CourierList";
import { TrailPanel } from "../components/tracking/TrailPanel";
import { CourierMarkers, TrailLayer, FitTo, PlaybackMarker } from "../components/tracking/CourierMapLayers";
import { STATUS, plParcels } from "../components/tracking/courierMapUtils";

const POLL_MS = 5000;
const CENTER = [52.0, 19.4];
const todayIso = () => { const d = new Date(); return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 10); };

const IdleBanner = ({ alerts, onSelect }) => {
  if (!alerts.length) return null;
  return (
    <div data-testid="idle-alerts-banner" className="px-4 md:px-5 py-2.5 bg-amber-50 border-b border-amber-200 flex flex-wrap items-center gap-2">
      <span className="inline-flex items-center gap-1.5 text-[11px] font-black uppercase tracking-wider text-amber-800"><AlertTriangle className="w-4 h-4" />Długi postój</span>
      {alerts.map((c) => (
        <button key={c.user_id} type="button" onClick={() => onSelect(c.user_id)} data-testid={`idle-alert-${c.user_id}`}
          className="h-7 px-3 rounded-full bg-white ring-1 ring-amber-300 text-[11px] font-bold text-amber-900 hover:bg-amber-100 transition-colors">
          {c.name} · {c.idle_min} min · {plParcels(c.progress.pending)}
        </button>
      ))}
    </div>
  );
};

export default function CourierMapPage() {
  const [params, setParams] = useSearchParams();
  const [couriers, setCouriers] = useState(null);
  const [selectedId, setSelectedId] = useState(params.get("courier"));
  const [date, setDate] = useState(todayIso());
  const [days, setDays] = useState([]);
  const [trail, setTrail] = useState(null);
  const [trailLoading, setTrailLoading] = useState(false);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [fitKey, setFitKey] = useState(0);
  const [showStops, setShowStops] = useState(true);
  const [playIdx, setPlayIdx] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [speed, setSpeed] = useState(4);
  const [idleThreshold, setIdleThreshold] = useState(20);
  const prevCount = useRef(0);
  const alerted = useRef(new Set());

  const fetchLive = useCallback(async () => {
    try {
      const { data } = await api.get("/tracking/live");
      setCouriers(data);
      setLastUpdate(new Date());
      const withPos = data.filter((c) => c.lat != null).length;
      if (withPos !== prevCount.current) { prevCount.current = withPos; setFitKey((k) => k + 1); }
      data.filter((c) => c.idle_alert && !alerted.current.has(c.user_id)).forEach((c) => {
        alerted.current.add(c.user_id);
        toast.warning(`${c.name}: postój ${c.idle_min} min w trakcie trasy`, { description: `${plParcels(c.progress.pending)} do dostarczenia` });
      });
      data.filter((c) => !c.idle_alert).forEach((c) => alerted.current.delete(c.user_id));
    } catch { setCouriers((c) => c || []); }
  }, []);

  useEffect(() => { fetchLive(); const t = setInterval(fetchLive, POLL_MS); return () => clearInterval(t); }, [fetchLive]);
  useEffect(() => { api.get("/tracking/settings").then((r) => setIdleThreshold(r.data.idle_alert_min)).catch(() => {}); }, []);

  const select = useCallback((id) => {
    setParams((p) => { const n = new URLSearchParams(p); if (id) n.set("courier", id); else n.delete("courier"); return n; }, { replace: true });
  }, [setParams]);

  useEffect(() => { setSelectedId(params.get("courier")); }, [params]);

  useEffect(() => {
    if (!selectedId) { setTrail(null); setDays([]); return; }
    setDate(todayIso());
    api.get(`/tracking/${selectedId}/days`).then((r) => setDays(r.data)).catch(() => setDays([]));
  }, [selectedId]);

  useEffect(() => {
    if (!selectedId) return;
    let alive = true;
    setTrailLoading(true);
    setPlaying(false);
    setPlayIdx(0);
    const load = () => api.get(`/tracking/${selectedId}/trail`, { params: { date } })
      .then((r) => { if (alive) { setTrail(r.data); setTrailLoading(false); } })
      .catch(() => { if (alive) { setTrail(null); setTrailLoading(false); } });
    load();
    const t = date === todayIso() ? setInterval(load, POLL_MS * 2) : null;
    return () => { alive = false; if (t) clearInterval(t); };
  }, [selectedId, date]);

  useEffect(() => { if (trail?.points?.length) setFitKey((k) => k + 1); }, [trail?.user_id, trail?.date, trail?.points?.length === 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const selected = useMemo(() => couriers?.find((c) => c.user_id === selectedId) || null, [couriers, selectedId]);
  const fitPoints = useMemo(() => {
    if (selectedId) {
      const pts = [...(trail?.points || []), ...(showStops ? (trail?.stops || []).filter((s) => s.lat != null) : [])];
      if (selected?.lat != null) pts.push({ lat: selected.lat, lng: selected.lng });
      return pts;
    }
    return (couriers || []).filter((c) => c.lat != null);
  }, [couriers, selectedId, trail, selected, showStops]);

  if (couriers === null) return <PageSkeleton variant="grid" label="Ładowanie mapy kurierów…" />;

  const counts = couriers.reduce((a, c) => { a[c.status] = (a[c.status] || 0) + 1; return a; }, {});
  const idleAlerts = couriers.filter((c) => c.idle_alert);
  const playbackActive = !!trail && (playing || playIdx > 0);
  const playPoint = playbackActive ? trail.points[Math.min(playIdx, trail.points.length - 1)] : null;

  return (
    <div className="p-3 md:p-6" data-testid="courier-map-page">
      <div className="border border-zinc-900 bg-white">
        <div className="flex flex-wrap items-center justify-between gap-3 px-4 md:px-5 py-4 border-b border-zinc-200 bg-zinc-50">
          <div>
            <div className="font-label mb-1">// Mapa kurierów · pozycja z telefonu</div>
            <h1 className="font-heading font-black text-xl md:text-2xl uppercase tracking-tight flex items-center gap-2"><Radar className="w-6 h-6 text-brand" />Kurierzy na żywo</h1>
          </div>
          <div className="flex flex-wrap items-center gap-2 text-[11px] font-mono text-zinc-500">
            {Object.entries(STATUS).map(([k, s]) => (
              <span key={k} data-testid={`legend-${k}`} className={`inline-flex items-center gap-1.5 px-2 h-6 rounded-full ring-1 ${s.pill}`}>
                <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label} <b>{counts[k] || 0}</b>
              </span>
            ))}
            <span data-testid="live-last-update" className="ml-1">Odświeżono: {lastUpdate ? lastUpdate.toLocaleTimeString("pl-PL") : "—"}</span>
          </div>
        </div>
        <IdleBanner alerts={idleAlerts} onSelect={select} />

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px]">
          <div className="relative h-[55vh] min-h-[360px] lg:h-[640px]">
            <MapContainer center={CENTER} zoom={6} scrollWheelZoom style={{ height: "100%", width: "100%" }} data-testid="courier-map-container">
              <TileLayer attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>' url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
              <FitTo points={fitPoints} trigger={fitKey} />
              <TrailLayer trail={trail} showStops={showStops} playIdx={playbackActive ? playIdx : null} />
              <PlaybackMarker point={playPoint} name={selected?.name || ""} />
              <CourierMarkers couriers={couriers} selectedId={selectedId} onSelect={select} />
            </MapContainer>
            {!couriers.some((c) => c.lat != null) && (
              <div data-testid="map-empty-overlay" className="absolute inset-x-0 bottom-4 flex justify-center pointer-events-none z-[400]">
                <div className="bg-zinc-900/90 text-white text-xs font-bold px-4 py-2 rounded-full backdrop-blur">Brak pozycji kurierów do wyświetlenia</div>
              </div>
            )}
          </div>
          <aside className="border-t lg:border-t-0 lg:border-l border-zinc-200 flex flex-col lg:max-h-[640px] lg:overflow-y-auto">
            <div className={selectedId ? "max-h-64 lg:max-h-56 overflow-y-auto shrink-0" : "flex-1 overflow-y-auto"}>
              <CourierList couriers={couriers} selectedId={selectedId} onSelect={select} />
            </div>
            {selectedId && (
              <TrailPanel courier={selected} days={days} date={date} onDate={setDate} trail={trail} loading={trailLoading} onClose={() => select(null)}
                showStops={showStops} onShowStops={setShowStops}
                playback={{ idx: playIdx, onIdx: setPlayIdx, playing, onPlaying: setPlaying, speed, onSpeed: setSpeed }} />
            )}
          </aside>
        </div>
        <div className="px-4 md:px-5 py-3 border-t border-zinc-200 bg-zinc-50 text-[11px] font-mono text-zinc-500 flex flex-wrap gap-4">
          <span>// Pozycja z przeglądarki telefonu kuriera · odświeżanie co 5 s</span>
          <span data-testid="idle-threshold-note">// Alert postoju: ≥ {idleThreshold} min w jednym miejscu przy niedostarczonych paczkach</span>
          <span>// Telefon wysyła pozycję, gdy aplikacja jest otwarta i kurier zezwolił na lokalizację</span>
        </div>
      </div>
    </div>
  );
}
